import React, {useCallback, useMemo, useState} from "react";
import {twJoin} from "tailwind-merge";
import {Button, defaultBorderColor, Icon} from "peggirkit";
import {PosOrderSummary} from "@/data/entities/order";
import {usePos} from "@/context/pos/PosProvider";
import OrdersList from "@widgets/pos/orders/sidebar/list/ordersList";
import SearchInput from "@widgets/pos/orders/sidebar/search/searchInput";
import FulfillmentFilterButtons, {
    FulfillmentFilter
} from "@widgets/pos/orders/sidebar/fulfillment/fulfillmentFilterButtons";
import {filterOrders} from "@/utils/PosSearch";
import DateFilter from "@widgets/pos/orders/sidebar/date/dateFilter";
import {FunnelIcon} from "@heroicons/react/20/solid";
import {FormattedMessage} from "react-intl";

export type RemoveFilterFn = () => void;

type Props = {
    selectedPublicCartId: string | undefined,
};

const OrdersSidebar = ({selectedPublicCartId}: Props) => {
    const {orders, selectedDay, setSelectedDay} = usePos();
    const [searchQuery, setSearchQuery] = useState("");
    const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentFilter>("ALL");
    const hasActiveFilter: boolean = useMemo(
        () => selectedDay !== null || searchQuery.length > 0 || fulfillmentFilter !== "ALL",
        [selectedDay, searchQuery, fulfillmentFilter]);
    const filteredOrders: PosOrderSummary[] = useMemo(
        () => filterOrders({
            orders,
            searchQuery,
            fulfillmentFilter,
        }),
        [orders, searchQuery, fulfillmentFilter]);
    const removeFilterFn = useCallback(() => {
        setSearchQuery("");
        setFulfillmentFilter("ALL");
        setSelectedDay(null);
    }, [setSearchQuery, setFulfillmentFilter, setSelectedDay]);

    return (
        <section
            className={twJoin(
                "min-h-0 flex flex-col bg-white dark:bg-slate-900 border-r",
                defaultBorderColor,
            )}
        >
            <div
                className={twJoin(
                    "shrink-0 p-4 space-y-3 border-b flex flex-col items-center justify-start",
                    defaultBorderColor,
                )}
            >
                <DateFilter/>

                <SearchInput
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />

                <FulfillmentFilterButtons
                    fulfillmentFilter={fulfillmentFilter}
                    setFulfillmentFilter={setFulfillmentFilter}
                />

                {
                    hasActiveFilter &&
                    <Button
                        size={"xs"}
                        variant={"outline"}
                        color={"danger"}
                        iconBefore={<Icon icon={FunnelIcon}/>}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: () => removeFilterFn(),
                            },
                        }}
                    >
                        <FormattedMessage id={"removeAllFilters"}/>
                    </Button>
                }
            </div>

            <OrdersList
                filteredOrders={filteredOrders}
                selectedPublicCartId={selectedPublicCartId}
                hasActiveFilter={hasActiveFilter}
                removeFilterFn={removeFilterFn}
            />
        </section>
    );
};

export default OrdersSidebar;